import { ColumnDef, Table } from '@g17eco/molecules/table';
import { UserWithInfo, Workgroup } from '@g17eco/types/workgroup';
import { useState } from 'react';
import { Input } from 'reactstrap';
import { AddUserBtn } from './AddUserBtn';
import IconButton from '@g17eco/molecules/button/IconButton';
import { useRemoveWorkgroupUserMutation } from '@api/workgroups';
import { generateErrorToast, generateToast } from '@g17eco/molecules/toasts';
import { Loader } from '@g17eco/atoms/loader';
import { UserNodesModal } from './UserNodesModal';

interface Props {
  workgroup: Workgroup<UserWithInfo>;
  initiativeId: string;
}

export const ManageUsers = ({ workgroup, initiativeId }: Props) => {
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithInfo | null>(null);

  const users = workgroup.users.filter(
    (user) =>
      user.name.toLowerCase().includes(search.toLowerCase()) || user.email.toLowerCase().includes(search.toLowerCase())
  );

  const [removeWorkgroupUser, { isLoading: isRemovingUser }] = useRemoveWorkgroupUserMutation();
  const handleRemove = (userId: string) => {
    removeWorkgroupUser({ initiativeId, workgroupId: workgroup._id, userId })
      .unwrap()
      .then(() => {
        generateToast({
          title: 'User removed',
          color: 'success',
          message: 'User has been removed from the workgroup',
        });
      })
      .catch((error) => {
        generateErrorToast(error);
      });
  };

  const handleCloseNodesModal = () => {
    setSelectedUser(null);
  };

  const columns: ColumnDef<UserWithInfo>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
    },
    {
      header: 'Email address',
      accessorKey: 'email',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        return (
          <div className='d-flex justify-content-end gap-2'>
            <IconButton
              icon='fal fa-eye text-secondary'
              color='link'
              onClick={() => setSelectedUser(row.original)}
              title='View accessible nodes'
              aria-label='View accessible nodes'
            />
            <IconButton
              icon='fal fa-trash text-danger'
              color='link'
              onClick={() => handleRemove(row.original._id)}
              disabled={isRemovingUser}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {isRemovingUser ? <Loader /> : null}
      <div className='d-flex gap-3 justify-content-between mt-5 mb-3'>
        <Input
          placeholder='Search for user'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={workgroup.users.length === 0}
        />
        <AddUserBtn workgroup={workgroup} initiativeId={initiativeId} />
      </div>
      <Table data={users} columns={columns} pageSize={10} responsive />
      <div className='d-flex justify-content-center mt-3'>
        <AddUserBtn
          workgroup={workgroup}
          initiativeId={initiativeId}
          buttonConfig={{ label: 'Add users to workgroup', color: 'secondary' }}
        />
      </div>
      <UserNodesModal user={selectedUser} initiativeId={initiativeId} onClose={handleCloseNodesModal} />
    </>
  );
};
